import {
  IonContent,
  IonPage,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from "@ionic/react";
import React from "react";
import clsx from "clsx";
import { CSVLink } from "react-csv";

import { getDateString } from "../../../libs/Util";
import MenuHeader from "../../../organisms/teacher/Header/MenuHeader";
import {
  Context,
  fetchSummaryPerClassStudents,
  fetchRealizationsCSVDataByKlass,
  updateStudentDetailModal,
} from "../../../store/teacher";
import { EMPTY_CLASS_MESSAGE } from "../../../constants/message";
import AnalyzeClassDetail from "../../../templates/teacher/AnalyzeClassDetail";
import useWaitApiCall from "../../../hooks/useWaitApiCall";
import { REALIZATIONS_CSV_HEADERS } from "../../../constants/analyzeConstant";
import useDidMountEffect from "../../../hooks/useDidMountEffect";
import styles from "../common.module.scss";
import { convertRealizationsToCsv } from "../../../libs/convert_realizations_to_csv";

export enum ESpan {
  WEEK = "week",
  MONTH = "month",
  YEAR = "year",
}

export enum ECond {
  TOTAL = "total",
  REALIZATION_COUNT = "realization_count",
  CONVERTED_REALIZATION_COUNT = "converted_realization_count",
  EXECUTE_REALIZATION_COUNT = "executed_realization_count",
  FREQUENCY_DATE_TIME_UNIX = "frequency_date_time_unix",
}

const AnalyzePage = () => {
  const { dispatch, contextState } = React.useContext(Context);
  const [calling, peep] = useWaitApiCall(contextState);
  const initialState = React.useMemo(
    () => ({
      selectedClassName: contextState.teacher.classes[0]?.full_name,
      selectedClassID: contextState.teacher.classes[0]?.id,
      span: ESpan.MONTH,
      cond: ECond.TOTAL,
      prevCond: ECond.TOTAL,
      isDesc: true,
    }),
    [contextState.teacher.classes],
  );
  const [values, updateValues] = React.useState<{
    selectedClassName: string;
    selectedClassID: number;
    span: ESpan;
    cond: ECond;
    prevCond: ECond;
    isDesc: boolean;
  }>({
    ...initialState,
  });
  const hasNoClasses =
    contextState.teacher.id !== 0 && contextState.teacher.classes.length === 0;

  const csvLinkRef = React.useRef<
    CSVLink & HTMLAnchorElement & { link?: HTMLAnchorElement }
  >(null);

  const update = (args: { [key: string]: any }) => {
    updateValues(prevValues => ({ ...prevValues, ...args }));
  };

  const fetchSummaryPerClassStudentsFunc = React.useCallback(
    (class_id: number, span: string) => {
      dispatch(fetchSummaryPerClassStudents(class_id, span));
    },
    [dispatch],
  );

  useIonViewWillEnter(() => {
    update({
      ...initialState,
    });
  }, [initialState]);

  React.useEffect(() => {
    update({
      selectedClassName: contextState.teacher.classes[0]?.full_name,
      selectedClassID: contextState.teacher.classes[0]?.id,
    });
  }, [contextState.teacher.classes]);

  React.useEffect(() => {
    if (values.selectedClassID)
      fetchSummaryPerClassStudentsFunc(values.selectedClassID, values.span);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.selectedClassID, values.span]);

  const getRealizationsCSVDataFunc = React.useCallback(
    () =>
      dispatch(peep(fetchRealizationsCSVDataByKlass(values.selectedClassID))),
    [dispatch, peep, values.selectedClassID],
  );

  const csvData = React.useMemo(() => {
    if (!contextState.realizations_csv_data_by_klass) {
      return [];
    }
    const { realizations } = contextState.realizations_csv_data_by_klass;
    return convertRealizationsToCsv(realizations);
  }, [contextState.realizations_csv_data_by_klass]);

  useDidMountEffect(() => {
    const timeout = setTimeout(() => {
      csvData.length &&
        csvLinkRef.current?.link &&
        csvLinkRef.current.props.data.length &&
        csvLinkRef.current.link.click();
    });
    return () => clearTimeout(timeout);
  }, [csvData]);

  useIonViewWillLeave(() => {
    dispatch(updateStudentDetailModal(null));
  });

  const onClickStudentFunc = React.useCallback(
    (student_id, name, photo) => {
      dispatch(
        updateStudentDetailModal({
          student_id,
          name,
          photo,
          class_id: values.selectedClassID,
          class_name: values.selectedClassName,
        }),
      );
    },
    [dispatch, values.selectedClassID, values.selectedClassName],
  );

  return (
    <IonPage>
      <MenuHeader title="生徒一覧" />
      <CSVLink
        ref={csvLinkRef}
        filename={`gear_download_${
          contextState.realizations_csv_data_by_klass?.school.name
        }_${
          contextState.teacher.classes.find(
            cl => cl.id === values.selectedClassID,
          )?.full_name
        }_${getDateString()}.csv`}
        headers={REALIZATIONS_CSV_HEADERS}
        data={csvData}
        uFEFF
        hidden
      />
      <IonContent className={clsx(styles.wrapper, styles.bgWhileBase)}>
        {contextState.teacher.classes.length > 0 && (
          <div className={styles.container}>
            <AnalyzeClassDetail
              calling={calling}
              values={values}
              classes={contextState.teacher.classes}
              class_students_summaries={contextState.class_students_summaries}
              update={update}
              getRealizations4CSV={getRealizationsCSVDataFunc}
              onClickStudent={onClickStudentFunc}
            />
          </div>
        )}
        {hasNoClasses && (
          <div className={styles.container}>
            <p className={clsx(styles.positionMiddle, styles.message)}>
              {EMPTY_CLASS_MESSAGE}
            </p>
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default AnalyzePage;
